import cx from "classnames";
import { useRef, useState } from "react";

import { isNullish, useEventListener } from "@theorgchartdev/storybook";
import {
  getContainerHeight,
  getContainerOffset,
  getContainerWidth
} from "@/Utils/getContainerSize";

/**
 *
 * @param {*} props
 * @returns
 */
const Dragger = ({ children, initOffset, fixed, className }) => {
  const [activationCoords, setActiveCoords] = useState(null);
  const [offsetCoords, setOffsetCoords] = useState(() => {
    if (!isNullish(initOffset)) {
      return initOffset;
    }

    return {
      x: 0,
      y: 0
    };
  });

  const elementRef = useRef(null);
  const contentRef = useRef(null);
  const originRef = useRef({ x: 0, y: 0 });
  const containerSizeRef = useRef({ width: 0, height: 0, left: 0, top: 0 });

  const handleElementRef = node => {
    if (!node || elementRef.current) {
      return;
    }

    elementRef.current = node;
    const rect = node.getBoundingClientRect();
    originRef.current = {
      x: rect.x,
      y: rect.y
    };
  };

  useEventListener(
    "mousemove",
    e => {
      // Bitwise operation
      // Reference: https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
      const isPrimaryMouseClicked = (e.buttons & 1) === 1;

      if (!isPrimaryMouseClicked) {
        setActiveCoords(null);
        return;
      }

      const mouseX = e.clientX;
      const mouseY = e.clientY;

      const relX = mouseX - containerSizeRef.current.left;
      const relY = mouseY - containerSizeRef.current.top;

      const isXOutOfBounds = relX < 0 || relX > containerSizeRef.current.width;
      const isYOutOfBounds = relY < 0 || relY > containerSizeRef.current.height;
      if (isXOutOfBounds || isYOutOfBounds) {
        return;
      }

      setOffsetCoords({
        x: mouseX - activationCoords.x,
        y: mouseY - activationCoords.y
      });
    },
    { enabled: !isNullish(activationCoords) }
  );

  useEventListener("resize", () => {
    const rect = contentRef.current?.getBoundingClientRect();
    if (isNullish(rect)) {
      return;
    }

    const padding = 10;

    // Read the container size here rather than from containerSizeRef
    // The ref only holds the size captured at the last drag start, so it would be stale.
    const containerWidth = getContainerWidth();
    const containerHeight = getContainerHeight();
    const containerOffset = getContainerOffset();

    // rect is viewport-relative; shift into container space to compare against
    // the container-relative bounds (correct when .appContainer is offset — MFE).
    const topY = rect.y - containerOffset.top;
    const rightX = rect.x + rect.width - containerOffset.left;
    const bottomY = rect.y + rect.height - containerOffset.top;
    const leftX = rect.x - containerOffset.left;

    const adjustOffset = (axis, condition, adjustment) => {
      if (!condition) {
        return;
      }

      setOffsetCoords(prev => ({
        ...prev,
        [axis]: prev[axis] + adjustment
      }));
    };

    adjustOffset("y", topY + padding > containerHeight, -rect.height);
    adjustOffset("x", rightX + padding < 0, rect.width);
    adjustOffset("y", bottomY + padding < 0, rect.height);
    adjustOffset("x", leftX + padding > containerWidth, -rect.width);
  });

  const handleMouseClick = e => {
    if (e.target.classList.contains("ocn-overlay-bookend")) {
      const containerOffset = getContainerOffset();
      containerSizeRef.current = {
        width: getContainerWidth(),
        height: getContainerHeight(),
        left: containerOffset.left,
        top: containerOffset.top
      };
      setActiveCoords({
        x: e.clientX - offsetCoords.x,
        y: e.clientY - offsetCoords.y
      });
    }
  };

  const style = {
    left: offsetCoords.x,
    top: offsetCoords.y,
    position: fixed ? "fixed" : "relative"
  };

  let childrenNode = children;
  if (typeof children === "function") {
    childrenNode = children(contentRef);
  }

  return (
    <div
      className={cx("ocn-dragger", className)}
      ref={handleElementRef}
      onMouseDown={handleMouseClick}
      style={style}
    >
      {childrenNode}
    </div>
  );
};

export default Dragger;
